import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Paper, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, CircularProgress,
  Snackbar, Alert, Chip, Tooltip as MuiTooltip,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import { getSuspendedClients } from "../api/adminApi";
import ReplaceWABADialog from "../components/ReplaceWABADialog";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
];

const STATUS_MAP = {
  suspended: { label: "Suspended", color: "#ef4444" },
  blocked: { label: "Blocked by Meta", color: "#ef4444" },
  api_error: { label: "API Error", color: "#f97316" },
  unverified: { label: "Unverified", color: "#f59e0b" },
  no_token: { label: "No Token", color: "#f59e0b" },
};

const isToday = (d) => {
  const now = new Date();
  return d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
};

const isYesterday = (d) => {
  const y = new Date();
  y.setDate(y.getDate() - 1);
  return d.getDate() === y.getDate() && d.getMonth() === y.getMonth() && d.getFullYear() === y.getFullYear();
};

const isThisWeek = (d) => (Date.now() - d.getTime()) <= 7 * 24 * 60 * 60 * 1000;

const isThisMonth = (d) => {
  const now = new Date();
  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
};

const matchFilter = (dateStr, filter) => {
  if (filter === "all") return true;
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return false;
  if (filter === "today") return isToday(d);
  if (filter === "yesterday") return isYesterday(d);
  if (filter === "week") return isThisWeek(d);
  if (filter === "month") return isThisMonth(d);
  return true;
};

const WhatsAppSuspended = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState("all");
  const [selectedClient, setSelectedClient] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const fetchClients = async () => {
    setLoading(true);
    try {
      const data = await getSuspendedClients();
      setClients(data.clients || []);
    } catch (err) {
      setSnack({ open: true, message: err.message, severity: "error" });
    }
    setLoading(false);
  };

  useEffect(() => { fetchClients(); }, []);

  const filteredClients = useMemo(() => {
    return clients.filter((c) => matchFilter(c.whatsapp?.suspendedAt, dateFilter));
  }, [clients, dateFilter]);

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
        <BlockIcon sx={{ color: "#ef4444", fontSize: 28 }} />
        <Typography fontWeight={800} fontSize={24} color="#1e293b">Recently Suspended</Typography>
      </Box>
      <Typography variant="body2" color="#64748b" mb={3}>WhatsApp accounts suspended by admin or blocked by Meta — replace WABA to restore access</Typography>

      <Paper className="glass-card" sx={{ overflow: "hidden" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, borderBottom: "1px solid #e2e8f0" }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            {FILTERS.map((f) => (
              <Chip key={f.key} label={f.label} size="small" clickable
                onClick={() => setDateFilter(f.key)}
                sx={{
                  fontWeight: 600, fontSize: 12, borderRadius: 1.5,
                  bgcolor: dateFilter === f.key ? "#ef4444" : "#f1f5f9",
                  color: dateFilter === f.key ? "#fff" : "#64748b",
                  "&:hover": { bgcolor: dateFilter === f.key ? "#dc2626" : "#e2e8f0" },
                  cursor: "pointer",
                }} />
            ))}
          </Box>
          <Typography variant="caption" color="#94a3b8">
            Showing {filteredClients.length} of {clients.length} clients
          </Typography>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Name</TableCell>
                <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Email</TableCell>
                <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Status</TableCell>
                <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Business</TableCell>
                <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Phone</TableCell>
                <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Detected On</TableCell>
                <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredClients.map((c, i) => {
                const st = STATUS_MAP[c.status] || { label: c.status || "—", color: "#64748b" };
                return (
                  <TableRow key={c._id || i}>
                    <TableCell sx={{ color: "#1e293b", borderColor: "#e2e8f0" }}>{c.fullName}</TableCell>
                    <TableCell sx={{ color: "#475569", borderColor: "#e2e8f0" }}>{c.email}</TableCell>
                    <TableCell sx={{ borderColor: "#e2e8f0" }}>
                      <MuiTooltip title={c.statusDetail || ""} arrow>
                        <Chip label={st.label} size="small" sx={{ color: "#fff", bgcolor: st.color, fontWeight: 600, fontSize: 11 }} />
                      </MuiTooltip>
                    </TableCell>
                    <TableCell sx={{ color: "#1e293b", borderColor: "#e2e8f0" }}>{c.whatsapp?.businessName || c.whatsapp?.companyName || "—"}</TableCell>
                    <TableCell sx={{ color: "#475569", borderColor: "#e2e8f0" }}>{c.whatsapp?.whatsappBusinessNumber || c.whatsapp?.businessPhone || "—"}</TableCell>
                    <TableCell sx={{ color: "#475569", borderColor: "#e2e8f0" }}>
                      {c.whatsapp?.suspendedAt ? new Date(c.whatsapp.suspendedAt).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell sx={{ borderColor: "#e2e8f0" }}>
                      <Button size="small" variant="contained" onClick={() => setSelectedClient(c)}
                        sx={{ bgcolor: "#ef4444", "&:hover": { bgcolor: "#dc2626" }, textTransform: "none", borderRadius: 1.5, fontSize: 12 }}>
                        Replace WABA
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredClients.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ color: "#64748b", textAlign: "center", borderColor: "#e2e8f0" }}>
                    {clients.length === 0 ? "No suspended or blocked clients" : `No results for "${FILTERS.find(f => f.key === dateFilter)?.label}"`}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <ReplaceWABADialog
        open={!!selectedClient}
        client={selectedClient}
        onClose={() => setSelectedClient(null)}
        onSuccess={() => { setSelectedClient(null); fetchClients(); }}
      />

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: "100%", borderRadius: 2 }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default WhatsAppSuspended;
